import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  MessageSquare, Send, Search, RefreshCw, Loader2,
  Building2, MessageCircle, Clock, Paperclip, X, FileText, Image as ImageIcon,
  Download, Bell, ExternalLink, File, FileSpreadsheet, Archive, Film,
} from 'lucide-react';
import axios from 'axios';
import { useWebSocket } from '../hooks/useWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dosya tipine göre ikon ve renk
const getFileIcon = (type, name = '') => {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (type?.startsWith('image/')) return { Icon: ImageIcon, color: 'text-green-500' };
  if (type === 'application/pdf' || ext === 'pdf') return { Icon: FileText, color: 'text-red-500' };
  if (['doc','docx'].includes(ext) || type?.includes('word')) return { Icon: FileText, color: 'text-blue-600' };
  if (['xls','xlsx'].includes(ext) || type?.includes('spreadsheet') || type?.includes('excel')) return { Icon: FileSpreadsheet, color: 'text-green-600' };
  if (['ppt','pptx'].includes(ext) || type?.includes('presentation')) return { Icon: File, color: 'text-orange-500' };
  if (['zip','rar','7z'].includes(ext)) return { Icon: Archive, color: 'text-yellow-600' };
  if (['mp4','avi','mov','mkv'].includes(ext) || type?.startsWith('video/')) return { Icon: Film, color: 'text-purple-500' };
  if (['dwg','dxf'].includes(ext)) return { Icon: File, color: 'text-teal-600' };
  return { Icon: File, color: 'text-gray-500' };
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Web Audio API bildirim sesi (harici dosya gerektirmez)
let audioCtx = null;
const playNotificationSound = () => {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, audioCtx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.12, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.25);
    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.25);
  } catch { /* ignore audio errors */ }
};

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const MessagingPage = ({ role }) => {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const autoConvId = searchParams.get('conv');

  const [wsConnected, setWsConnected] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({}); // { [conv_id]: number }
  const activeConvRef = useRef(null); // shadow ref for WS handler (avoids stale closure)

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const bottomRef = useRef(null);
  // pollRef is provided by the useWebSocket hook
  const fileRef = useRef(null);

  // File attachment state — multiple files
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{ name, path, type, size }]
  const [uploadingFile, setUploadingFile] = useState(false);

  const myOrgId = user?.org_memberships?.[0]?.org_id || '';
  const myOrgIdRef = useRef(myOrgId);
  useEffect(() => { myOrgIdRef.current = myOrgId; }, [myOrgId]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - pendingAttachments.length;
    const toUpload = files.slice(0, remaining);
    if (!toUpload.length) { alert('En fazla 5 dosya ekleyebilirsiniz.'); return; }
    const oversized = toUpload.find(f => f.size > 200 * 1024 * 1024);
    if (oversized) { alert(`"${oversized.name}" çok büyük (maks. 200MB)`); return; }
    setUploadingFile(true);
    try {
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
      const uploaded = await Promise.all(toUpload.map(async (file) => {
        if (file.size <= CHUNK_SIZE) {
          // Small file — direct upload
          const formData = new FormData();
          formData.append('file', file);
          const res = await axios.post(`${API}/upload`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
          return { name: file.name, path: res.data.path, type: file.type, size: file.size };
        } else {
          // Large file — chunked upload
          const initRes = await axios.post(`${API}/upload/chunked/init`,
            { filename: file.name, file_size: file.size, content_type: file.type },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const { upload_id } = initRes.data;
          const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
          for (let i = 0; i < totalChunks; i++) {
            const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const fd = new FormData();
            fd.append('chunk', chunk, `chunk_${i}`);
            await axios.post(`${API}/upload/chunked/${upload_id}/chunk?chunk_index=${i}`, fd, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
          }
          const completeRes = await axios.post(`${API}/upload/chunked/${upload_id}/complete`, {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return { name: file.name, path: completeRes.data.path, type: file.type, size: file.size };
        }
      }));
      setPendingAttachments(prev => [...prev, ...uploaded]);
    } catch { alert('Dosya yüklenemedi'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const loadConversations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data.conversations || []);
    } catch { /* ignore */ }
    finally { setConvLoading(false); }
  }, [token]);

  const openConversation = useCallback(async (conv) => {
    setActiveConv(conv);
    // Reset unread badge
    setUnreadCounts(prev => { const n = { ...prev }; delete n[conv.id]; return n; });
    setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_for_me: 0 } : c));
    setMsgLoading(true);
    try {
      const res = await axios.get(`${API}/messages/conversations/${conv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
    } catch { setMessages([]); }
    finally { setMsgLoading(false); }
  }, [token]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Auto-open conversation from URL param
  useEffect(() => {
    if (autoConvId && conversations.length > 0) {
      const c = conversations.find(c => c.id === autoConvId);
      if (c && (!activeConv || activeConv.id !== autoConvId)) openConversation(c);
    }
  }, [autoConvId, conversations, activeConv, openConversation]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket real-time connection via custom hook
  const { connectWebSocket: wsConnect, disconnectWebSocket, pollRef } = useWebSocket({
    token,
    onConnect: () => { setWsConnected(true); },
    onDisconnect: () => { setWsConnected(false); },
    onMessage: (data, convId) => {
      if (data.type === 'message') {
        const msgConvId = data.message?.conv_id || convId;
        const isFromMe = data.message?.sender_org_id === myOrgIdRef.current;
        const isActive = activeConvRef.current?.id === msgConvId;
        if (isActive) {
          setMessages(prev => {
            const exists = prev.some(m => m.id === data.message.id);
            if (exists) return prev.map(m => m.id === data.message.id ? data.message : m);
            const filtered = prev.filter(m =>
              !(m.optimistic && m.content === data.message.content && m.sender_id === data.message.sender_id)
            );
            return [...filtered, data.message];
          });
        }
        setConversations(prev => prev.map(c =>
          c.id === msgConvId
            ? { ...c, last_message_preview: data.message.content?.slice(0, 80), unread_for_me: isActive ? 0 : (c.unread_for_me || 0) + (isFromMe ? 0 : 1) }
            : c
        ));
        if (!isActive && !isFromMe) {
          setUnreadCounts(prev => ({ ...prev, [msgConvId]: (prev[msgConvId] || 0) + 1 }));
          playNotificationSound();
        }
      }
    },
    onPoll: async (convId) => {
      try {
        const res = await axios.get(`${API}/messages/conversations/${convId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessages(res.data.messages || []);
      } catch { /* ignore */ }
    },
  });

  const connectWebSocket = useCallback((convId) => {
    wsConnect(convId);
  }, [wsConnect]);

  // Keep shadow ref in sync
  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);
  useEffect(() => {
    if (activeConv) {
      connectWebSocket(activeConv.id);
    } else {
      disconnectWebSocket();
    }
  }, [activeConv?.id]); // eslint-disable-line

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!input.trim() && !pendingAttachments.length) || !activeConv) return;
    setSending(true);
    const content = input.trim();
    const attachments = [...pendingAttachments];
    setInput('');
    setPendingAttachments([]);
    // Optimistic update
    const optimisticMsg = {
      id: `tmp_${Date.now()}`,
      sender_id: user?.id || '',
      sender_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
      content,
      attachments,
      created_at: new Date().toISOString(),
      optimistic: true,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    try {
      await axios.post(
        `${API}/messages/conversations/${activeConv.id}/send`,
        { content, attachments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Reload to get confirmed message
      const res = await axios.get(`${API}/messages/conversations/${activeConv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
      await loadConversations();
    } catch { /* keep optimistic */ }
    finally { setSending(false); }
  };

  const accentColor = role === 'CONTRACTOR' ? 'bg-green-600' : 'bg-blue-600';
  const accentLight = role === 'CONTRACTOR' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700';

  const navigate = useNavigate();
  const briefDetailPath = role === 'CONTRACTOR'
    ? '/app/contractor/brief-board'
    : role === 'SUPPLIER'
    ? '/app/supplier/find-briefs'
    : '/app/exhibitor/briefs';

  const filteredConvs = conversations.filter(c =>
    !search || (c.other_party_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.brief_title || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-8rem)] bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Left: Conversations - hidden on mobile when conv is active */}
      <div className={`${activeConv ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 md:shrink-0 border-r border-gray-100`}>
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-gray-500" />Conversations
            </h2>
            <div className="flex items-center gap-2">
              {Object.values(unreadCounts).reduce((a, b) => a + b, 0) > 0 && (
                <span className="flex items-center gap-1 text-xs text-blue-600 font-semibold bg-blue-50 px-2 py-0.5 rounded-full">
                  <Bell className="h-3 w-3" />
                  {Object.values(unreadCounts).reduce((a, b) => a + b, 0)} new
                </span>
              )}
              <button onClick={loadConversations}
                className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {convLoading ? (
            <div className="flex items-center justify-center h-20">
              <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
            </div>
          ) : filteredConvs.length === 0 ? (
            <div className="p-6 text-center">
              <MessageCircle className="h-8 w-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No conversations yet</p>
              <p className="text-xs text-gray-300 mt-1">
                {role === 'CONTRACTOR'
                  ? 'Click "Message" on a proposal to start chatting'
                  : 'Click "Message" on a received proposal to start chatting'}
              </p>
            </div>
          ) : (
            filteredConvs.map(c => (
              <button
                key={c.id}
                data-testid={`conv-item-${c.id}`}
                onClick={() => openConversation(c)}
                className={`w-full text-left p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                  activeConv?.id === c.id ? 'bg-blue-50 border-l-4 border-l-blue-400' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Building2 className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="text-sm font-semibold text-gray-900 truncate">{c.other_party_name}</span>
                      {((unreadCounts[c.id] || 0) + (c.unread_for_me || 0)) > 0 && (
                        <span className="ml-auto shrink-0 bg-blue-600 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold px-1 animate-bounce">
                          {(unreadCounts[c.id] || 0) + (c.unread_for_me || 0)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{c.brief_title}</p>
                    {c.last_message_preview && (
                      <p className={`text-xs truncate mt-0.5 ${((unreadCounts[c.id] || 0) + (c.unread_for_me || 0)) > 0 ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                        {c.last_message_preview}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    <span className="text-xs text-gray-400">{fmt(c.last_message_at)}</span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right: Message thread - hidden on mobile when no conv selected */}
      <div className={`${activeConv ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}>
        {!activeConv ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <MessageSquare className="h-8 w-8 text-gray-300" />
            </div>
            <p className="text-base font-semibold text-gray-500">Select a conversation</p>
            <p className="text-sm text-gray-400 mt-1 max-w-xs">
              Choose a conversation from the list, or start one from a brief or proposal page.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setActiveConv(null)}
                  className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{activeConv.other_party_name}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    {activeConv.brief_title}
                    {wsConnected && (
                      <span className="ml-1 inline-flex items-center gap-1 text-emerald-500">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                        Live
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Brief Context Banner */}
            {activeConv?.brief_id && activeConv.brief_title && activeConv.brief_title !== 'Brief' && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center justify-between gap-2"
                data-testid="brief-context-banner">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <span className="text-xs font-semibold text-amber-700 truncate">
                    Brief: {activeConv.brief_title}
                  </span>
                </div>
                <button
                  onClick={() => navigate(briefDetailPath)}
                  className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-semibold whitespace-nowrap shrink-0"
                  data-testid="view-brief-link">
                  Görüntüle <ExternalLink className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {msgLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-200" />
                  <p className="text-sm">No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map(msg => {
                  const isMe = msg.sender_id === (user?.id || '');
                  return (
                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                        {!isMe && (
                          <span className="text-xs text-gray-400 ml-1">{msg.sender_name}</span>
                        )}
                        <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                          isMe
                            ? `${accentColor} text-white rounded-tr-sm`
                            : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                        } ${msg.optimistic ? 'opacity-60' : ''}`}>
                          {msg.content}
                          {/* Attachments */}
                          {(msg.attachments || []).map((att, ai) => {
                            const isImage = att.type?.startsWith('image/');
                            const backendUrl = process.env.REACT_APP_BACKEND_URL;
                            const fileUrl = att.path ? `${backendUrl}/api/files/${att.path}` : null;
                            const { Icon, color } = getFileIcon(att.type, att.name);
                            return (
                              <div key={ai} className="mt-2">
                                {isImage && fileUrl ? (
                                  <a href={fileUrl} target="_blank" rel="noreferrer" className="block">
                                    <img
                                      src={fileUrl}
                                      alt={att.name}
                                      className="max-w-[220px] max-h-[160px] rounded-xl object-cover cursor-zoom-in border border-white/20"
                                      onError={e => { e.target.style.display='none'; }}
                                    />
                                    <p className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-gray-400'}`}>{att.name}</p>
                                  </a>
                                ) : (
                                  <div className={`rounded-xl p-2.5 flex items-center gap-2.5 ${isMe ? 'bg-white/20' : 'bg-white border border-gray-100'}`}>
                                    <Icon className={`w-4 h-4 shrink-0 ${isMe ? 'text-white/80' : color}`} />
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-xs font-semibold truncate ${isMe ? 'text-white' : 'text-gray-800'}`}>{att.name}</p>
                                      {att.size && <p className={`text-xs ${isMe ? 'text-white/60' : 'text-gray-400'}`}>{fmtSize(att.size)}</p>}
                                    </div>
                                    {fileUrl && (
                                      <a href={fileUrl} download={att.name} target="_blank" rel="noreferrer"
                                        className={`p-1 rounded-lg ${isMe ? 'hover:bg-white/30' : 'hover:bg-gray-100'} transition-colors`}
                                        title="İndir">
                                        <Download className={`w-3.5 h-3.5 ${isMe ? 'text-white/80' : 'text-gray-500'}`} />
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className={`flex items-center gap-1 text-xs text-gray-400 ${isMe ? 'justify-end' : 'justify-start'} ml-1`}>
                          <Clock className="h-2.5 w-2.5" />
                          {fmt(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-gray-100 bg-white">
              {/* Pending attachments preview */}
              {pendingAttachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {pendingAttachments.map((att, i) => {
                    const { Icon, color } = getFileIcon(att.type, att.name);
                    return (
                      <div key={i} className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-xl px-2.5 py-1.5 max-w-[200px]">
                        <Icon className={`h-3.5 w-3.5 shrink-0 ${color}`} />
                        <span className="text-xs font-medium text-blue-800 truncate flex-1">{att.name}</span>
                        <button onClick={() => setPendingAttachments(prev => prev.filter((_, j) => j !== i))} className="text-blue-400 hover:text-red-500 shrink-0">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <form onSubmit={sendMessage} className="flex items-center gap-2">
                {/* File upload button */}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingFile || pendingAttachments.length >= 5}
                  className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-400 hover:text-blue-600 hover:border-blue-300 transition-colors shrink-0 disabled:opacity-40 relative"
                  title={pendingAttachments.length >= 5 ? 'Maks. 5 dosya' : 'Dosya ekle'}
                >
                  {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  {pendingAttachments.length > 0 && !uploadingFile && (
                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {pendingAttachments.length}
                    </span>
                  )}
                </button>
                <input ref={fileRef} type="file" className="hidden" onChange={handleFileSelect} multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.dwg,.dxf"
                  title="Maks. 200MB, 5 dosya" />
                <input
                  data-testid="message-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder={pendingAttachments.length ? "Mesaj ekle (isteğe bağlı)..." : "Mesaj yazın..."}
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                />
                <button
                  data-testid="send-message-btn"
                  type="submit"
                  disabled={(!input.trim() && !pendingAttachments.length) || sending}
                  className={`w-10 h-10 rounded-xl ${accentColor} disabled:opacity-40 text-white flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity`}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagingPage;
